import React, { useState, useEffect } from 'react';
import { Settings2, Plus, Trash2, Save, Package } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function HitachiConfigPanel({ projectId, isAdmin = true }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editSeatTypes, setEditSeatTypes] = useState([]);
  const [editPreCompleted, setEditPreCompleted] = useState(0);

  useEffect(() => {
    if (!projectId) return;
    axios.get(`${API}/api/hitachi/config/${projectId}`)
      .then(res => {
        setConfig(res.data);
        setEditSeatTypes(res.data.seat_types || []);
        setEditPreCompleted(res.data.pre_completed_sets || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  const handleSave = async () => {
    try {
      await axios.put(`${API}/api/hitachi/config/${projectId}`, {
        seat_types: editSeatTypes,
        pre_completed_sets: parseInt(editPreCompleted) || 0,
      });
      toast.success('Hitachi config updated');
      setConfig(prev => ({ ...prev, seat_types: editSeatTypes, pre_completed_sets: parseInt(editPreCompleted) || 0 }));
      setEditing(false);
    } catch (err) {
      toast.error('Failed to update config');
    }
  };

  const addSeatType = () => {
    setEditSeatTypes(prev => [...prev, { material_code: '', name: '', qty_per_set: 0, type: 'seat' }]);
  };

  const removeSeatType = (index) => {
    setEditSeatTypes(prev => prev.filter((_, i) => i !== index));
  };

  const updateSeatType = (index, field, value) => {
    setEditSeatTypes(prev => prev.map((st, i) => i === index ? { ...st, [field]: value } : st));
  };

  if (loading || !config) return null;

  const totalPerSet = (config.seat_types || []).reduce((s, st) => s + (st.qty_per_set || 0), 0);
  const seatCount = (config.seat_types || []).filter(st => st.type === 'seat').length;
  const otherCount = (config.seat_types || []).filter(st => st.type === 'other').length;

  return (
    <div className="mt-3 border-t border-indigo-100 pt-2" data-testid="hitachi-config-panel">
      <button onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-[11px] font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
        data-testid="hitachi-config-toggle">
        <Settings2 className="w-3.5 h-3.5" />
        Hitachi Set Configuration
        <span className="text-[9px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full">
          {seatCount} seat{seatCount !== 1 ? 's' : ''} + {otherCount} other | {totalPerSet}/set | Pre: {config.pre_completed_sets}
        </span>
      </button>

      {expanded && (
        <div className="mt-2 bg-indigo-50/50 rounded-lg border border-indigo-100 p-3">
          {/* Pre-completed sets */}
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Package className="w-3.5 h-3.5 text-indigo-500" />
              <span className="text-[10px] font-bold text-slate-600 uppercase">Pre-completed Sets:</span>
              {editing ? (
                <input type="number" min="0" value={editPreCompleted}
                  onChange={e => setEditPreCompleted(e.target.value)}
                  className="w-16 border border-indigo-300 rounded px-2 py-0.5 text-xs font-bold text-center focus:outline-none focus:ring-1 focus:ring-indigo-400"
                  data-testid="pre-completed-input" />
              ) : (
                <span className="text-sm font-bold text-indigo-700">{config.pre_completed_sets}</span>
              )}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              {isAdmin && !editing && (
                <button onClick={() => setEditing(true)}
                  className="text-[10px] px-2.5 py-1 bg-indigo-100 text-indigo-600 rounded font-semibold hover:bg-indigo-200"
                  data-testid="hitachi-config-edit-btn">Edit</button>
              )}
              {editing && (
                <>
                  <button onClick={handleSave}
                    className="text-[10px] px-2.5 py-1 bg-emerald-600 text-white rounded font-semibold hover:bg-emerald-700 flex items-center gap-1"
                    data-testid="hitachi-config-save-btn"><Save className="w-3 h-3" /> Save</button>
                  <button onClick={() => { setEditing(false); setEditSeatTypes(config.seat_types); setEditPreCompleted(config.pre_completed_sets); }}
                    className="text-[10px] px-2 py-1 text-slate-400 hover:text-slate-600">Cancel</button>
                </>
              )}
            </div>
          </div>

          {/* Seat types table */}
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-[9px] text-slate-400 uppercase tracking-wider border-b border-indigo-200">
                <th className="py-1 text-left font-semibold">Material Code</th>
                <th className="py-1 text-left font-semibold">Name</th>
                <th className="py-1 text-center font-semibold w-14">Qty/Set</th>
                <th className="py-1 text-center font-semibold w-14">Type</th>
                {editing && <th className="py-1 text-center font-semibold w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {(editing ? editSeatTypes : config.seat_types || []).map((st, i) => (
                <tr key={i} className="border-b border-indigo-100/50">
                  <td className="py-1.5">
                    {editing ? (
                      <input value={st.material_code} onChange={e => updateSeatType(i, 'material_code', e.target.value)}
                        className="w-full border border-slate-200 rounded px-1.5 py-0.5 text-[10px] font-mono" />
                    ) : (
                      <span className="text-[10px] font-mono text-slate-500">{st.material_code}</span>
                    )}
                  </td>
                  <td className="py-1.5">
                    {editing ? (
                      <input value={st.name} onChange={e => updateSeatType(i, 'name', e.target.value)}
                        className="w-full border border-slate-200 rounded px-1.5 py-0.5 text-[10px]" />
                    ) : (
                      <span className="font-medium text-slate-700">{st.name}</span>
                    )}
                  </td>
                  <td className="py-1.5 text-center">
                    {editing ? (
                      <input type="number" min="0" value={st.qty_per_set}
                        onChange={e => updateSeatType(i, 'qty_per_set', parseInt(e.target.value) || 0)}
                        className="w-12 border border-slate-200 rounded px-1 py-0.5 text-center text-[10px] font-bold" />
                    ) : (
                      <span className="font-bold text-indigo-700">{st.qty_per_set}</span>
                    )}
                  </td>
                  <td className="py-1.5 text-center">
                    {editing ? (
                      <select value={st.type} onChange={e => updateSeatType(i, 'type', e.target.value)}
                        className="border border-slate-200 rounded px-1 py-0.5 text-[10px]">
                        <option value="seat">Seat</option>
                        <option value="other">Other</option>
                      </select>
                    ) : (
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${st.type === 'other' ? 'bg-slate-100 text-slate-500' : 'bg-blue-50 text-blue-600'}`}>
                        {st.type === 'other' ? 'OTHER' : 'SEAT'}
                      </span>
                    )}
                  </td>
                  {editing && (
                    <td className="py-1.5 text-center">
                      <button onClick={() => removeSeatType(i)} className="p-0.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-indigo-200">
                <td colSpan={2} className="py-1.5 font-bold text-slate-600">Total per Set</td>
                <td className="py-1.5 text-center font-bold text-indigo-700">
                  {(editing ? editSeatTypes : config.seat_types || []).reduce((s, st) => s + (st.qty_per_set || 0), 0)}
                </td>
                <td colSpan={editing ? 2 : 1}></td>
              </tr>
            </tfoot>
          </table>
          {editing && (
            <button onClick={addSeatType}
              className="mt-2 text-[10px] font-semibold text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
              data-testid="add-seat-type-btn">
              <Plus className="w-3 h-3" /> Add Seat Type
            </button>
          )}
        </div>
      )}
    </div>
  );
}
